/**
 * @file useUndoStack.ts
 * @description «Отменить / Вернуть» в конструкторе страниц (phase19.16 Р2;
 * uiux-design-r2 §9): последние 20 действий сеанса на открытой странице —
 * добавление, удаление, дублирование, перемещение, скрытие блока и
 * сохранённая правка блока. Отмена — обратный вызов API; с Р3а (SB126) она
 * меняет черновик страницы, а не сайт.
 *
 * Запись — пара обратимых действий (`undo` / `redo`), её собирает тот, кто
 * выполнил действие (`useBlockActions`). Новая запись очищает «Вернуть».
 * Смена страницы (`resetKey`) очищает историю. Ошибка отмены — запись
 * пропадает (вернуть её уже нельзя), текст ошибки — через `onError`.
 * Горячие клавиши — `useUndoHotkeys`: Ctrl+Z / Ctrl+Shift+Z (и Ctrl+Y), не
 * в полях ввода (там работает отмена самого поля).
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** Сколько действий помнит история (uiux-design-r2 §9). */
export const UNDO_LIMIT = 20;

export interface UndoEntry {
  /** Что сделано: «Добавлен блок «Фото справа в край»». */
  label: string;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

export interface UndoApi {
  push: (entry: UndoEntry) => void;
  undo: () => Promise<boolean>;
  redo: () => Promise<boolean>;
  canUndo: boolean;
  canRedo: boolean;
  /** Что отменится следующим (подсказка кнопки). */
  undoLabel: string | null;
  redoLabel: string | null;
  /** Идёт отмена или возврат — кнопки выключены. */
  busy: boolean;
  clear: () => void;
}

interface Options {
  /** Смена ключа (другая страница) очищает историю. */
  resetKey?: string | null;
  limit?: number;
  onError?: (err: unknown, kind: "undo" | "redo") => void;
}

export function useUndoStack({ resetKey = null, limit = UNDO_LIMIT, onError }: Options = {}): UndoApi {
  const past = useRef<UndoEntry[]>([]);
  const future = useRef<UndoEntry[]>([]);
  const [, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const rerender = useCallback(() => setVersion((v) => v + 1), []);
  const errorRef = useRef(onError);
  errorRef.current = onError;

  const clear = useCallback(() => {
    past.current = [];
    future.current = [];
    rerender();
  }, [rerender]);

  useEffect(() => {
    clear();
  }, [resetKey, clear]);

  const push = useCallback(
    (entry: UndoEntry) => {
      past.current = [...past.current, entry].slice(-limit);
      future.current = [];
      rerender();
    },
    [limit, rerender],
  );

  /** Общий шаг: взять запись из одного стека, выполнить, положить в другой. */
  const step = useCallback(
    async (kind: "undo" | "redo"): Promise<boolean> => {
      const from = kind === "undo" ? past : future;
      const to = kind === "undo" ? future : past;
      const entry = from.current[from.current.length - 1];
      if (!entry || busyRef.current) return false;
      busyRef.current = true;
      setBusy(true);
      from.current = from.current.slice(0, -1);
      try {
        await entry[kind]();
        to.current = [...to.current, entry].slice(-limit);
        return true;
      } catch (err) {
        errorRef.current?.(err, kind);
        return false;
      } finally {
        busyRef.current = false;
        setBusy(false);
        rerender();
      }
    },
    [limit, rerender],
  );

  const undo = useCallback(() => step("undo"), [step]);
  const redo = useCallback(() => step("redo"), [step]);

  return {
    push,
    undo,
    redo,
    canUndo: past.current.length > 0 && !busy,
    canRedo: future.current.length > 0 && !busy,
    undoLabel: past.current[past.current.length - 1]?.label ?? null,
    redoLabel: future.current[future.current.length - 1]?.label ?? null,
    busy,
    clear,
  };
}

/** Фокус в поле ввода: там Ctrl+Z отменяет набор текста, а не действие. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** Открыто модальное окно кабинета — его хоткеи не наши. */
export function modalOpen(): boolean {
  return document.querySelector("[data-slot=dialog-content], [data-slot=alert-dialog-content], [role=dialog][aria-modal=true]") !== null;
}

/** Ctrl+Z — отменить, Ctrl+Shift+Z / Ctrl+Y — вернуть (Cmd на Mac). */
export function useUndoHotkeys(api: Pick<UndoApi, "undo" | "redo">, enabled = true): void {
  const latest = useRef(api);
  latest.current = api;
  useEffect(() => {
    if (!enabled) return;
    function onKey(event: KeyboardEvent) {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || isEditableTarget(event.target) || modalOpen()) return;
      // По месту клавиши, а не букве: в русской раскладке Z — «я».
      const z = event.code === "KeyZ";
      const y = event.code === "KeyY";
      if (!z && !y) return;
      const redo = (z && event.shiftKey) || y;
      event.preventDefault();
      void (redo ? latest.current.redo() : latest.current.undo());
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}
